import * as React from 'react';
import {
  isMobileStyleMixin,
  TextButton,
  TouchLink,
  TouchLinkGroup,
  useIsMobile,
} from '@amzn/storm-ui';
import styled, { css } from 'styled-components';
import { IA2AdvertisingAccountItem } from './IA2AdvertisingAccountItem';
import { IA2AccountDropdownList, AccountTypeLabel } from './styles';
import { concrete, mercury } from '../../../../colors';
import { NoAccountsMessage } from './NoAccountsMessage';
import { IA2MobileAccountListItem } from './IA2MobileAccountListItem';
import { useAccountDropdownBundle } from '../../../../providers/PantherLocalizationProvider';
import { getCountyCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { MessageBundle } from '@amzn/arb-tools';
import { Account, AdvertisingAccountType } from '@amzn/aac-chrome-internal-api';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

export const PAGE_SIZE = 30;

const MULTI_MARKETPLACE_ACCOUNT_TYPES = [
  AdvertisingAccountType.GLOBAL,
  AdvertisingAccountType.DSP_ENTITY,
];

const ACCOUNT_TYPE_LABEL_MAPPING = new Map([
  [AdvertisingAccountType.DSP_ADVERTISER, 'account-dropdown-type-dsp'],
  [AdvertisingAccountType.DSP_ENTITY, 'account-dropdown-type-dsp'],
  [AdvertisingAccountType.SPONSORED_ADS, 'account-dropdown-type-sponsored-ads'],
  [AdvertisingAccountType.MANAGER, 'account-dropdown-type-manager'],
  [AdvertisingAccountType.AMC, 'account-switcher-filter-amc-label'],
]);

type AccountTypeSectionProps = {
  accounts: Account[];
  label: string;
  type: AdvertisingAccountType;
  selectedEntityId?: string;
  isLastAccountType?: boolean;
};

export const IA2AccountListItem = styled.li<{
  isSelected: boolean;
}>`
  list-style: none;

  ${({ isSelected }) => {
    return isSelected ? `background-color: ${concrete};` : '';
  }}

  &:hover {
    background-color: ${mercury};
  }
`;

export const AccountTypeSectionContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.base};
`;

export const PaginationButton = styled(TextButton)<{
  extraPaddingAtBottom?: boolean;
}>`
  text-align: left;
  display: block;

  ${({ extraPaddingAtBottom }) =>
    extraPaddingAtBottom
      ? css`
          padding-bottom: ${({ theme }) => theme.spacing.base};
        `
      : ''}}

  ${isMobileStyleMixin(css`
    padding-top: ${({ theme }) => theme.spacing.base};
  `)};
`;

export const AccountTypeLabelContainer = styled.div<{ withMargin?: boolean }>`
  margin-inline-start: ${({ withMargin, theme }) =>
    withMargin === true ? theme.spacing.base : ''};
  margin-inline-end: ${({ withMargin, theme }) =>
    withMargin === true ? theme.spacing.base : ''};
`;

const getCountryLabel = ({
  accountType,
  account,
  bundle,
}: {
  accountType: AdvertisingAccountType;
  account: Account;
  bundle: MessageBundle;
}) => {
  if (
    accountType === AdvertisingAccountType.MANAGER ||
    accountType === AdvertisingAccountType.DSP_ENTITY
  ) {
    return '';
  }

  if (MULTI_MARKETPLACE_ACCOUNT_TYPES.includes(accountType)) {
    return bundle.getMessage('account-switcher-multiple-countries');
  }

  let marketplaceId = account.marketplaceId;
  if (
    accountType === AdvertisingAccountType.SPONSORED_ADS &&
    Array.isArray(account.nestedAccounts)
  ) {
    if (account.nestedAccounts.length > 1) {
      return bundle.getMessage('account-switcher-multiple-countries');
    }

    if (account.nestedAccounts.length === 1) {
      marketplaceId = account.nestedAccounts[0].marketplaceId;
    }
  }

  if (!marketplaceId) {
    return '';
  }

  const countryCode = getCountyCodeByMarketplace(marketplaceId);

  if (countryCode) {
    return bundle.getMessage(`account-switcher-marketplace-${countryCode}`);
  }

  // fallback to not rendering anything
  return '';
};

const IA2DesktopAccountDropdownList: React.FC<{
  accounts: Account[];
  accountType: AdvertisingAccountType;
  selectedEntityId?: string;
  listReference: React.RefObject<HTMLUListElement>;
}> = ({ accounts, selectedEntityId, accountType, listReference }) => {
  const bundle = useAccountDropdownBundle();
  const isMagellanEnabled = useIsFeatureEnabled(
    WeblabedFeatures.MAGELLAN_RETAILER_HEADER,
  );

  return (
    <IA2AccountDropdownList ref={listReference}>
      {accounts.map((account: Account) => {
        const isSelected = selectedEntityId === account.entityId;

        return (
          <IA2AccountListItem key={account.url} isSelected={isSelected}>
            <IA2AdvertisingAccountItem
              isSelected={isSelected}
              advertisingAccount={account}
              key={account.entityId}
              retailerName={
                isMagellanEnabled ? account.retailerName : undefined
              }
              countryLabel={getCountryLabel({
                accountType,
                account,
                bundle,
              })}
            />
          </IA2AccountListItem>
        );
      })}
    </IA2AccountDropdownList>
  );
};

const IA2MobileAccountDropdownList: React.FC<{
  accounts: Account[];
  accountType: AdvertisingAccountType;
}> = ({ accounts, accountType }) => {
  const bundle = useAccountDropdownBundle();
  return (
    <TouchLinkGroup>
      {accounts.map((account) => {
        return (
          <TouchLink
            key={account.url}
            href={account.url}
            data-ccx-e2e-id="aac-account-link"
            data-takt-id="aac-account-link"
          >
            <IA2MobileAccountListItem
              key={account.url}
              accountLabel={account.label}
              countryLabel={getCountryLabel({
                accountType,
                account,
                bundle,
              })}
              retailerName={account.retailerName}
            />
          </TouchLink>
        );
      })}
    </TouchLinkGroup>
  );
};

export const IA2AccountDropdownTypeSection: React.FC<
  AccountTypeSectionProps
> = ({ accounts, label, type, selectedEntityId, isLastAccountType }) => {
  const isMobile = useIsMobile();
  const [selectedPage, setSelectedPage] = React.useState(1);
  const bundle = useAccountDropdownBundle();
  const pages = Math.ceil(accounts.length / PAGE_SIZE);
  const selectedEndIndex = selectedPage * PAGE_SIZE;
  const listReference = React.useRef<HTMLUListElement>(null);
  const isMagellanEnabled = useIsFeatureEnabled(
    WeblabedFeatures.MAGELLAN_RETAILER_HEADER,
  );

  // reset selected page if number of accounts change
  React.useEffect(() => {
    setSelectedPage(1);
  }, [accounts.length]);

  const paginate = () => {
    const previousPage = selectedPage;
    setSelectedPage(selectedPage + 1);
    setTimeout(() => {
      const focusElement =
        listReference.current &&
        listReference.current.children[previousPage * PAGE_SIZE];
      if (focusElement && focusElement.firstChild) {
        (focusElement.firstChild as HTMLElement).focus();
      }
    });
  };

  return (
    <AccountTypeSectionContainer>
      <AccountTypeLabelContainer withMargin={isMagellanEnabled && !isMobile}>
        <AccountTypeLabel>{label}</AccountTypeLabel>
      </AccountTypeLabelContainer>
      {(!accounts || accounts.length == 0) && <NoAccountsMessage />}
      {accounts && accounts.length > 0 && !isMobile && (
        <IA2DesktopAccountDropdownList
          selectedEntityId={selectedEntityId}
          accountType={type}
          accounts={accounts.slice(0, selectedEndIndex)}
          listReference={listReference}
        />
      )}
      {accounts && accounts.length > 0 && isMobile && (
        <IA2MobileAccountDropdownList
          accounts={accounts.slice(0, selectedEndIndex)}
          accountType={type}
        />
      )}
      {pages > 1 && selectedPage !== pages && (
        <PaginationButton
          onClick={paginate}
          extraPaddingAtBottom={isLastAccountType}
        >
          {(() => {
            const labelKey = ACCOUNT_TYPE_LABEL_MAPPING.get(type);
            if (labelKey) {
              return bundle.formatMessage(
                'account-dropdown-account-type-pagination',
                {
                  pageSize: PAGE_SIZE.toString(),
                  accountType: bundle.getMessage(labelKey),
                },
              );
            }

            return bundle.formatMessage(
              'account-dropdown-invalid-account-type-pagination',
              {
                pageSize: PAGE_SIZE.toString(),
              },
            );
          })()}
        </PaginationButton>
      )}
    </AccountTypeSectionContainer>
  );
};
